import React, { useEffect, useMemo, useState } from 'react';
import {
  Briefcase,
  CheckCircle2,
  Clock3,
  FileWarning,
  MessageCircle,
  Plane,
  RefreshCw,
  UserCheck,
  Users
} from 'lucide-react';
import APIService from '../services/APIService';
import EmptyState from './EmptyState';
import LoadingSpinner from './LoadingSpinner';

const defaultStats = {
  total_applications: 0,
  new_applications: 0,
  pending_applications: 0,
  under_review_applications: 0,
  approved_applications: 0,
  rejected_applications: 0,
  ready_to_travel: 0,
  traveled: 0,
  returned: 0,
  missing_documents: 0,
  new_messages: 0,
  total_jobs: 0,
  active_jobs: 0,
  total_clients: 0,
  clients_in_processing: 0,
  cancelled_or_inactive: 0,
  recent_applications: []
};

const cardConfig = [
  { key: 'total_applications', label: 'Total Applications', icon: Users, color: 'bg-blue-50 text-blue-700' },
  { key: 'new_applications', label: 'New Applications', icon: Clock3, color: 'bg-cyan-50 text-cyan-700' },
  { key: 'pending_applications', label: 'Pending Applications', icon: FileWarning, color: 'bg-amber-50 text-amber-700' },
  { key: 'under_review_applications', label: 'Under Review', icon: RefreshCw, color: 'bg-indigo-50 text-indigo-700' },
  { key: 'approved_applications', label: 'Approved', icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700' },
  { key: 'rejected_applications', label: 'Rejected', icon: FileWarning, color: 'bg-rose-50 text-rose-700' },
  { key: 'ready_to_travel', label: 'Ready To Travel', icon: Plane, color: 'bg-green-50 text-green-700' },
  { key: 'traveled', label: 'Traveled', icon: Plane, color: 'bg-purple-50 text-purple-700' },
  { key: 'returned', label: 'Returned', icon: UserCheck, color: 'bg-slate-100 text-slate-700' },
  { key: 'missing_documents', label: 'Missing Documents', icon: FileWarning, color: 'bg-orange-50 text-orange-700' },
  { key: 'new_messages', label: 'Unread Messages', icon: MessageCircle, color: 'bg-fuchsia-50 text-fuchsia-700' },
  { key: 'active_jobs', label: 'Active Jobs', icon: Briefcase, color: 'bg-sky-50 text-sky-700' }
];

const AdminAnalyticsTab = () => {
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await APIService.getAdminDashboardStats();
        setStats({ ...defaultStats, ...(response || {}) });
      } catch (err) {
        setStats(defaultStats);
        setError(err.message || 'Failed to load analytics.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const recentApplications = useMemo(
    () => (Array.isArray(stats.recent_applications) ? stats.recent_applications : []),
    [stats.recent_applications]
  );

  if (loading) {
    return <LoadingSpinner message="Loading analytics..." />;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h2>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          Track application volume, travel readiness, client communication, and missing documents without leaving the
          admin portal.
        </p>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cardConfig.map((card) => {
          const Icon = card.icon;
          const value = Number(stats?.[card.key] || 0);
          return (
            <div key={card.key} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-500">{card.label}</p>
                  <p className="mt-3 text-3xl font-bold text-slate-900">{value}</p>
                </div>
                <div className={`rounded-2xl p-3 ${card.color}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Recent Applications</h3>
              <p className="mt-1 text-sm text-slate-600">The latest client applications and their current workflow stage.</p>
            </div>
          </div>

          {recentApplications.length === 0 ? (
            <div className="mt-6">
              <EmptyState
                icon={Users}
                title="No applications yet"
                description="When clients begin applying online, the latest applications will appear here for quick review."
                tone="soft"
              />
            </div>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                    <th className="pb-3 pr-4">Applicant</th>
                    <th className="pb-3 pr-4">Job Interest</th>
                    <th className="pb-3 pr-4">Application Status</th>
                    <th className="pb-3 pr-4">Travel Status</th>
                    <th className="pb-3">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {recentApplications.map((application) => (
                    <tr key={application.client_id}>
                      <td className="py-3 pr-4 font-medium text-slate-900">{application.name}</td>
                      <td className="py-3 pr-4">{application.interested_job_category || 'Not set'}</td>
                      <td className="py-3 pr-4">{String(application.application_status || 'draft').replace(/_/g, ' ')}</td>
                      <td className="py-3 pr-4">{String(application.client_lifecycle_status || 'new_lead').replace(/_/g, ' ')}</td>
                      <td className="py-3">{application.created_at ? new Date(application.created_at).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Operations Snapshot</h3>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span>Total clients</span>
                <span className="font-semibold text-slate-900">{stats.total_clients || 0}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span>Clients in processing</span>
                <span className="font-semibold text-slate-900">{stats.clients_in_processing || 0}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span>Verified clients</span>
                <span className="font-semibold text-slate-900">{stats.verified_clients || 0}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span>Cancelled or inactive</span>
                <span className="font-semibold text-slate-900">{stats.cancelled_or_inactive || 0}</span>
              </div>
              <div className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3">
                <span>Total jobs</span>
                <span className="font-semibold text-slate-900">{stats.total_jobs || 0}</span>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-slate-900">Health Check</h3>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
              <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 px-4 py-3 text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                <span>Dashboard metrics stay visible even when counts are zero.</span>
              </div>
              <div className="flex items-center gap-3 rounded-2xl bg-blue-50 px-4 py-3 text-blue-700">
                <RefreshCw className="h-4 w-4" />
                <span>Recent applications fall back to a clean empty state when there is no data.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsTab;
